import { RiPencilLine } from "@itandi/itandi-bb-ui";
import { Buttonv2 } from "@itandi/itandi-bb-ui/Buttonv2";
import { IconButton } from "@itandi/itandi-bb-ui/experimental/IconButton";
import { Form } from "@itandi/itandi-bb-ui/Form";
import { FormControl } from "@itandi/itandi-bb-ui/FormControl";
import { FormGroup } from "@itandi/itandi-bb-ui/FormGroup";
import { InputDatev2 } from "@itandi/itandi-bb-ui/InputDatev2";
import { ModalBody } from "@itandi/itandi-bb-ui/ModalBody";
import { ModalFooter } from "@itandi/itandi-bb-ui/ModalFooter";
import { ModalHeader } from "@itandi/itandi-bb-ui/ModalHeader";
import { Modalv2 } from "@itandi/itandi-bb-ui/Modalv2";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import { TextLink } from "@itandi/itandi-bb-ui/TextLink";
import { useReadiedQuery } from "@itandi/tools/next";
import { parsePositiveInteger } from "@itandi/tools/vanilla";
import { useId, useState } from "react";
import useSWRMutation from "swr/mutation";
import useItandiAccount from "~/hooks/useItandiAccount";
import { propertyDealCacheKey } from "~/hooks/usePropertyDeals";
import type { DealWithPrices } from "~/models/Deal";
import { isSuccess } from "~/utils/api/api_handler";
import { update as updateExpectedAccountingDate } from "~/utils/api/deals/DealExpectedAccountingDate";
import { formatDate, formatHyphenDate, toDate } from "~/utils/DateUtil";
import { getSkFullPath, PATH } from "~/utils/SeisanKanriPath";
import Styles from "./RelatedDealRow.module.scss";

type Props = {
	deal: DealWithPrices;
	notTargetDeal: boolean;
};

export function RelatedDealRowSkLink({ deal, notTargetDeal }: Props) {
	const { notify } = useNotification();
	const formId = useId();
	const id = useReadiedQuery("id", (q) => {
		const parsed = parsePositiveInteger(q);

		if (parsed === null) {
			throw new Error("Invalid Deal ID");
		}

		return parsed;
	});
	const { data: session } = useItandiAccount().currentAccount();
	const isV2DealDetail = session?.company?.useV2DealDetail ?? false;

	const [date, setDate] = useState<Date | null>(null);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const handleOpenModal = () => {
		if (deal.expectedAccountingDate) {
			setDate(toDate(deal.expectedAccountingDate));
		}
		setIsModalOpen(true);
	};
	const handleCloseModal = () => {
		setDate(null);
		setIsModalOpen(false);
	};

	const { trigger, isMutating } = useSWRMutation(
		propertyDealCacheKey(id),
		async (_key, { arg }: { arg: string | null }) =>
			updateExpectedAccountingDate(deal.id, arg)
	);

	const onSubmit = async () => {
		const result = await trigger(formatHyphenDate(date));
		if (isSuccess(result)) {
			handleCloseModal();
			notify({ content: "保存しました。", variant: "success" });
		} else {
			notify({
				content: result.message ?? "エラーが発生しました。",
				variant: "error"
			});
		}
	};

	return (
		<div className={Styles.SkWrapper}>
			<div className={Styles.SkLinkBlock}>
				<h4 className={Styles.ContextLabel}>精算管理登録</h4>
				{deal.skPartnerDeal?.linked ? (
					<TextLink
						color="link"
						href={getSkFullPath(PATH.partnerDeal(deal.skPartnerDeal.id))}
					>
						登録済
					</TextLink>
				) : !notTargetDeal ? (
					<TextLink
						color="link"
						href={`${getSkFullPath(PATH.partnerDealsNew)}?nskDealId=${deal.id}`}
					>
						登録
					</TextLink>
				) : null}
			</div>
			{isV2DealDetail ? (
				<div className={Styles.DateBlock}>
					<h4 className={Styles.ContextLabel}>計上予定日</h4>
					{deal.expectedAccountingDate !== null ? (
						<div className={Styles.ExpectedAccountingDateRow}>
							<span className={Styles.ContentText}>
								{formatDate(deal.expectedAccountingDate) ?? ""}
							</span>
							<IconButton
								aria-label="計上予定日を編集"
								Icon={RiPencilLine}
								onClick={handleOpenModal}
								size="s"
								variant="secondary"
							/>
						</div>
					) : !notTargetDeal ? (
						<Buttonv2
							onClick={handleOpenModal}
							size="s"
							variant="secondary"
							width="fit-content"
						>
							登録
						</Buttonv2>
					) : null}
					{isModalOpen ? (
						<Modalv2 isOpen onClose={handleCloseModal} size="s">
							<ModalHeader
								title={`計上予定日の${deal.expectedAccountingDate ? "編集" : "登録"}`}
							/>
							<ModalBody>
								<Form id={formId} onSubmit={onSubmit}>
									<FormGroup variant="vertical">
										<FormControl label="計上予定日">
											<InputDatev2 onChange={setDate} value={date} />
										</FormControl>
									</FormGroup>
								</Form>
							</ModalBody>
							<ModalFooter
								leftArea={
									<Buttonv2
										disabled={isMutating}
										onClick={handleCloseModal}
										variant="secondary"
									>
										キャンセル
									</Buttonv2>
								}
								rightArea={
									<Buttonv2
										form={formId}
										loading={isMutating}
										type="submit"
										variant="primary"
									>
										保存
									</Buttonv2>
								}
							/>
						</Modalv2>
					) : null}
				</div>
			) : null}
		</div>
	);
}
